import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { adminPortalAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Search, KeyRound, Trash2, User, Phone, Mail, Copy, Check, AlertTriangle } from 'lucide-react';

export default function PortalUsers() {
  const [loading, setLoading] = useState(true);
  const [portalUsers, setPortalUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [resetDialog, setResetDialog] = useState({ open: false, user: null, tempPassword: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPortalUsers();
  }, []);

  const fetchPortalUsers = async () => {
    try {
      const response = await adminPortalAPI.getAll();
      setPortalUsers(response.data);
    } catch (error) {
      toast.error('Failed to load portal users');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (user) => {
    try {
      const response = await adminPortalAPI.resetPassword(user.id);
      setResetDialog({ 
        open: true, 
        user, 
        tempPassword: response.data.temp_password,
        notificationSent: response.data.notification_sent
      });
      toast.success('Password reset successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteDialog.user) return;
    
    try {
      await adminPortalAPI.delete(deleteDialog.user.id);
      toast.success('Portal user deleted');
      setDeleteDialog({ open: false, user: null });
      fetchPortalUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Password copied to clipboard');
  };

  const filteredUsers = portalUsers.filter(u => 
    u.phone?.includes(search) ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.customer_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="portal-users-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Portal Users</h1>
          <p className="text-gray-500 mt-1">Manage customer portal accounts</p>
        </div>
      </div>

      {/* Search */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by phone, email, or customer name..."
              className="pl-10"
              data-testid="portal-users-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="h-20 bg-gray-200 rounded-lg animate-pulse" />)}
        </div>
      ) : filteredUsers.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-8 text-center">
            <User className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No portal users found</p>
            <p className="text-gray-400 text-sm mt-1">Customers register through the Customer Portal</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredUsers.map((user) => (
            <Card key={user.id} className="border-0 shadow-sm" data-testid={`portal-user-${user.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-lime-100 flex items-center justify-center">
                        <User className="w-5 h-5 text-lime-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-gray-900">
                          {user.customer_name || 'Unlinked Account'}
                        </h3>
                        {!user.customer_id && (
                          <span className="text-xs text-orange-500 font-medium">Not linked to customer</span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500 ml-13">
                      {user.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-4 h-4" />
                          {user.phone}
                        </span>
                      )}
                      {user.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {user.email}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => handleResetPassword(user)}
                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      data-testid={`reset-password-${user.id}`}
                    >
                      <KeyRound className="w-4 h-4 mr-2" />
                      Reset Password
                    </Button>
                    <Button 
                      variant="outline"
                      size="icon"
                      onClick={() => setDeleteDialog({ open: true, user })}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      data-testid={`delete-user-${user.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Reset Password Dialog */}
      <Dialog open={resetDialog.open} onOpenChange={(open) => !open && setResetDialog({ open: false, user: null, tempPassword: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-lime-500" />
              Password Reset Successful
            </DialogTitle>
            <DialogDescription>
              The password for {resetDialog.user?.customer_name || resetDialog.user?.phone || resetDialog.user?.email} has been reset.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-lime-50 rounded-lg border border-lime-200">
              <p className="text-sm text-gray-600 mb-2">New Temporary Password:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white p-3 rounded border text-lg font-mono font-bold text-lime-700">
                  {resetDialog.tempPassword}
                </code>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(resetDialog.tempPassword)}
                  className="shrink-0"
                >
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            
            {!resetDialog.notificationSent && (
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200 flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-yellow-800 font-medium">Notifications are not active</p>
                  <p className="text-xs text-yellow-700">Please share this password with the customer manually (call, text, etc.)</p>
                </div>
              </div>
            )}
            
            {resetDialog.notificationSent && (
              <p className="text-sm text-green-600">
                ✓ Notification sent to customer
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button onClick={() => setResetDialog({ open: false, user: null, tempPassword: null })}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, user: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Portal User
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this portal user account? They will need to register again to access the customer portal.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="font-medium text-gray-900">{deleteDialog.user?.customer_name || 'Unlinked Account'}</p>
              <p className="text-sm text-gray-500">{deleteDialog.user?.phone || deleteDialog.user?.email}</p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, user: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser} data-testid="confirm-delete-user">
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
